import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from '@fluentui/react-components';
import {
  AddRegular,
  ArrowUndoRegular,
  ArrowRedoRegular,
  MoreHorizontalRegular,
  SettingsRegular,
  GridRegular,
  CopyRegular,
  DeleteRegular,
  SlideLayoutRegular,
  ShareRegular,
} from '@fluentui/react-icons';
import { useTheme } from '../contexts/ThemeContext';
import { useDashboard } from '../contexts/DashboardContext';
import { useTopBarActions } from '../contexts/TopBarContext';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { setScroll } from '../hooks/scrollStore';
import { useLayoutEditor } from '../hooks/useLayoutEditor';
import { getLayoutHistoryShortcutLabels } from '../hooks/layoutHistoryShortcuts';
import type { LayoutItem } from 'react-grid-layout';
import { getCachedFilterData, invalidateDashboard } from './charts/hooks';
import { deriveFilterUiType } from '../api/types';
import type { FilterStats } from '../api/types';
import {
  getFilterValues,
  setFilterValue,
  deleteFilterValue,
  clearFilterValues,
  flattenFilterValues,
} from '../hooks/filterStore';
import { DashboardGridLayout } from './grid';
import { captureAndUploadThumbnail } from '../hooks/useThumbnailCapture';
import { DashboardFilterBar } from './DashboardFilterBar';
import DashboardSettingsPanel, { mergeDashboardSettings } from './DashboardSettingsPanel';
import { DashboardShareDialog } from './DashboardShareDialog';
import { chartsService } from '../api/chartsService';
import type { UpdateDashboardSettingsRequest } from '../api/types';
import { useThumbnailCapture } from '../hooks/useThumbnailCapture';
import { MutationOnly, useWorkspaceAccess } from './security/WorkspaceAccess';
import './Dashboard.css';

/** Filter types that are ready immediately — no upfront data fetch needed. */
function isFilterReadyImmediately(filter: { type: string; filterStats: FilterStats | null }): boolean {
  if (filter.type !== 'categorical') return true;
  const uiType = deriveFilterUiType(filter.type, filter.filterStats);
  if (uiType === 'search_select') return true;
  return false;
}

/**
 * Dashboard — renders a grid of charts with optional filters.
 * Owns the layout editor and sets TopBar actions directly.
 */
const Dashboard: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { isReadOnly, runMutation } = useWorkspaceAccess();

  const {
    currentDashboardDetails,
    isLoadingDetails,
    error: dashboardError,
    loadDashboardDetails,
    duplicateDashboard,
    deleteDashboard,
    updateLayoutInDetails,
    updateDashboard,
    updateDashboardSettings,
    updateDashboardInList,
    scrollToChartId,
    setScrollToChartId,
  } = useDashboard();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recompile = searchParams.get('recompile') === 'true';

  const dashboardId = currentDashboardDetails?.id ?? '';
  const filters = currentDashboardDetails?.filters || [];
  const charts = currentDashboardDetails?.charts || [];

  // ── Thumbnail capture ───────────────────────────────────────────────────
  const thumbnailCharts = useMemo(
    () => charts.map(c => ({
      id: c.id,
      chartType: c.chartType,
      orderIndex: c.orderIndex,
    })),
    [charts],
  );
  useThumbnailCapture(
    isReadOnly ? undefined : (dashboardId || undefined),
    thumbnailCharts,
  );

  // ── Dashboard settings (merged with defaults) ────────────────────────────
  const dashboardSettings = useMemo(
    () => mergeDashboardSettings(currentDashboardDetails?.settings),
    [currentDashboardDetails?.settings],
  );

  const widthMode = dashboardSettings.layout.width;

  // Force grid to recalculate width when mode changes
  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [widthMode]);

  // ── Settings panel state ─────────────────────────────────────────────────
  const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);

  const handleToggleSettingsPanel = useCallback(() => {
    setIsSettingsPanelOpen((prev) => !prev);
  }, []);

  const handleSettingsChange = useCallback(
    (partial: UpdateDashboardSettingsRequest) => {
      if (!dashboardId) return;
      updateDashboardSettings(dashboardId, partial);
    },
    [dashboardId, updateDashboardSettings],
  );

  // Handler for name/description changes from settings panel
  const handleDashboardMetaChange = useCallback(
    async (updates: { name?: string; description?: string }) => {
      if (!dashboardId) return;
      try {
        await updateDashboard(dashboardId, updates);
        if (updates.name) {
          updateDashboardInList(dashboardId, { name: updates.name });
        }
        if (updates.description !== undefined) {
          updateDashboardInList(dashboardId, { description: updates.description });
        }
      } catch (err) {
        console.error('Failed to update dashboard metadata:', err);
      }
    },
    [dashboardId, updateDashboard, updateDashboardInList],
  );

  // ── Layout editor (undo/redo/autosave/auto-arrange) ──────────────────────
  const layoutEditor = useLayoutEditor(dashboardId, charts);
  const layoutShortcutLabels = useMemo(() => getLayoutHistoryShortcutLabels(), []);

  // Wrap onLayoutChange to also patch the context cache so navigating
  // away and back doesn't flash the old layout.
  const handleLayoutChange = useCallback(
    (newLayout: LayoutItem[]) => {
      layoutEditor.onLayoutChange(newLayout);
      updateLayoutInDetails(
        newLayout.map((item) => ({ id: item.i, x: item.x, y: item.y, w: item.w, h: item.h })),
      );
    },
    [layoutEditor.onLayoutChange, updateLayoutInDetails],
  );

  // ── TopBar actions — rendered via ref to avoid infinite re-render ─────────
  // We store the latest layout editor values in a ref and build the actions
  // JSX only when truly needed (dashboard changes, not every render).

  const layoutRef = useRef(layoutEditor);
  layoutRef.current = layoutEditor;

  const handleAddChart = useCallback(() => {
    if (!dashboardId) return;
    navigate(`/dashboards/${dashboardId}/charts/new`);
  }, [dashboardId, navigate]);

  const handleDuplicate = useCallback(async () => {
    if (!dashboardId) return;
    const result = await duplicateDashboard(dashboardId);
    if (result) {
      navigate(`/dashboards/${result.id}`);
    }
  }, [dashboardId, duplicateDashboard, navigate]);

  const handleDelete = useCallback(async () => {
    if (!dashboardId) return;
    const nextId = await deleteDashboard(dashboardId);
    if (nextId === false) return;
    if (nextId) {
      navigate(`/dashboards/${nextId}`);
    } else {
      navigate('/dashboards');
    }
  }, [dashboardId, deleteDashboard, navigate]);

  // Wrap layout actions in stable callbacks that read from ref
  const handleUndo = useCallback(() => layoutRef.current.undo(), []);
  const handleRedo = useCallback(() => layoutRef.current.redo(), []);
  const handleAutoArrange = useCallback(() => layoutRef.current.autoArrange(), []);
  const handleToggleWidth = useCallback(() => {
    if (!dashboardId) return;
    const newWidth = widthMode === 'full' ? 'centered' : 'full';
    const layoutUpdate: Record<string, unknown> = { width: newWidth };
    if (newWidth === 'centered') layoutUpdate.maxWidth = 1600;
    updateDashboardSettings(dashboardId, { layout: layoutUpdate });
  }, [dashboardId, widthMode, updateDashboardSettings]);

  // Undo/redo availability — track as state so TopBar re-renders when it changes
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isSaving, setIsSavingLocal] = useState(false);

  useEffect(() => {
    setCanUndo(layoutEditor.canUndo);
  }, [layoutEditor.canUndo]);

  useEffect(() => {
    setCanRedo(layoutEditor.canRedo);
  }, [layoutEditor.canRedo]);

  useEffect(() => {
    setIsSavingLocal(layoutEditor.isSaving);
  }, [layoutEditor.isSaving]);

  const topBarActions = useMemo(() => (
    <MutationOnly>
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
      <Tooltip content="Add chart" relationship="label">
        <Button
          appearance="subtle"
          size="small"
          icon={<AddRegular />}
          onClick={handleAddChart}
          aria-label="Add chart"
        />
      </Tooltip>

      <Tooltip content={`Undo (${layoutShortcutLabels.undo})`} relationship="label">
        <Button
          appearance="subtle"
          size="small"
          icon={<ArrowUndoRegular />}
          onClick={handleUndo}
          disabled={!canUndo}
          aria-label="Undo"
        />
      </Tooltip>

      <Tooltip content={`Redo (${layoutShortcutLabels.redo})`} relationship="label">
        <Button
          appearance="subtle"
          size="small"
          icon={<ArrowRedoRegular />}
          onClick={handleRedo}
          disabled={!canRedo}
          aria-label="Redo"
        />
      </Tooltip>

      <Tooltip content="Dashboard settings" relationship="label">
        <Button
          appearance="subtle"
          size="small"
          icon={<SettingsRegular />}
          onClick={handleToggleSettingsPanel}
          aria-label="Dashboard settings"
        />
      </Tooltip>

      <Menu positioning="below-end">
        <MenuTrigger disableButtonEnhancement>
          <Tooltip content="More options" relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={<MoreHorizontalRegular />}
              aria-label="More options"
            />
          </Tooltip>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem icon={<SlideLayoutRegular />} onClick={handleToggleWidth}>
              {widthMode === 'full' ? 'Centered (1600px)' : 'Full width'}
            </MenuItem>
            <MenuItem icon={<GridRegular />} onClick={handleAutoArrange}>
              Auto arrange
            </MenuItem>
            <MenuItem icon={<CopyRegular />} onClick={handleDuplicate}>
              Duplicate dashboard
            </MenuItem>
            <MenuItem icon={<ShareRegular />} onClick={() => setIsShareDialogOpen(true)}>
              Share dashboard
            </MenuItem>
            <MenuItem icon={<DeleteRegular />} onClick={handleDelete}>
              Delete dashboard
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>

      {isSaving && (
        <Spinner size="tiny" aria-label="Saving layout" style={{ marginLeft: 4 }} />
      )}
      </div>
    </MutationOnly>
  ), [handleAddChart, handleUndo, handleRedo, handleAutoArrange, handleToggleWidth, handleToggleSettingsPanel, handleDuplicate, handleDelete, canUndo, canRedo, isSaving, widthMode, layoutShortcutLabels]);

  useTopBarActions(topBarActions);

  // ── Filter values (persisted per-dashboard via filterStore) ──────────────
  const [filterValues, setFilterValuesState] = useState<Map<string, string | string[] | boolean | Record<string, unknown> | null>>(
    () => (dashboardId ? getFilterValues(dashboardId) : new Map()),
  );

  // Sync local state when switching dashboards
  const prevDashboardIdRef = useRef(dashboardId);
  useEffect(() => {
    if (dashboardId && dashboardId !== prevDashboardIdRef.current) {
      setFilterValuesState(getFilterValues(dashboardId));
    }
    prevDashboardIdRef.current = dashboardId;
  }, [dashboardId]);

  const handleFilterChange = useCallback(
    (variable: string, value: any) => {
      setFilterValuesState((prev) => {
        const next = new Map(prev);
        if ((Array.isArray(value) && value.length === 0) || value === null) {
          next.delete(variable);
          deleteFilterValue(dashboardId, variable);
        } else {
          next.set(variable, value);
          setFilterValue(dashboardId, variable, value);
        }
        return next;
      });
    },
    [dashboardId],
  );

  const handleFilterReset = useCallback(
    (variable: string) => {
      setFilterValuesState((prev) => {
        if (!prev.has(variable)) return prev;
        const next = new Map(prev);
        next.delete(variable);
        deleteFilterValue(dashboardId, variable);
        return next;
      });
    },
    [dashboardId],
  );

  const handleResetAll = useCallback(() => {
    setFilterValuesState(new Map());
    clearFilterValues(dashboardId);
  }, [dashboardId]);

  // ── Default filter values ────────────────────────────────────────────────
  const defaultFilterValuesRef = useRef<Record<string, any>>({});

  const handleFilterDefault = useCallback(
    (variable: string, value: any) => {
      defaultFilterValuesRef.current[variable] = value;
      setRefreshVersion((v) => v + 1);
    },
    [],
  );

  const [refreshVersion, setRefreshVersion] = useState(0);

  const handleRefreshData = useCallback(() => {
    if (!dashboardId) return;
    invalidateDashboard(dashboardId);
    setRefreshVersion((v) => v + 1);
  }, [dashboardId]);

  // ── Filter readiness gate ────────────────────────────────────────────────
  const [readyFilterIds, setReadyFilterIds] = useState<Set<string>>(() => {
    const set = new Set<string>();
    if (!currentDashboardDetails) return set;
    for (const f of filters) {
      if (isFilterReadyImmediately(f)) {
        set.add(f.id);
      } else if (getCachedFilterData(dashboardId, f.id)) {
        set.add(f.id);
      }
    }
    return set;
  });

  const prevReadinessDashboardId = useRef<string | null>(null);
  useEffect(() => {
    const id = currentDashboardDetails?.id ?? null;
    if (id === prevReadinessDashboardId.current) return;
    prevReadinessDashboardId.current = id;

    if (!id || !currentDashboardDetails) {
      setReadyFilterIds(new Set());
      return;
    }

    const set = new Set<string>();
    const newFilters = currentDashboardDetails.filters || [];
    for (const f of newFilters) {
      if (isFilterReadyImmediately(f)) {
        set.add(f.id);
      } else if (getCachedFilterData(id, f.id)) {
        set.add(f.id);
      }
    }
    setReadyFilterIds(set);
  }, [currentDashboardDetails?.id, currentDashboardDetails]);

  const filtersReady = useMemo(() => {
    if (filters.length === 0) return true;
    return filters.every((f) => readyFilterIds.has(f.id));
  }, [filters, readyFilterIds]);

  const handleFilterReady = useCallback(
    (filterId: string) => {
      setReadyFilterIds((prev) => {
        if (prev.has(filterId)) return prev;
        const next = new Set(prev);
        next.add(filterId);
        return next;
      });
    },
    [filters.length],
  );

  // ── Scroll restoration ───────────────────────────────────────────────────
  const scrollRef = useRef<HTMLDivElement>(null);
  const isReady = !!charts?.length;

  useScrollRestoration(
    dashboardId ? `dashboard:${dashboardId}` : null,
    isReady,
    scrollRef,
    !!scrollToChartId,
  );

  // ── Scroll to newly created chart (one-shot signal from ChartEditPage) ──
  useEffect(() => {
    if (!scrollToChartId || !charts.length) return;
    const targetId = scrollToChartId;
    setScrollToChartId(null); // clear immediately — one-shot

    // Reset saved scroll position so restoration doesn't fight the scroll-to-chart
    if (dashboardId) setScroll(`dashboard:${dashboardId}`, 0);

    // Wait for grid to render the new chart element
    const tryScroll = (attempts = 0) => {
      const el = document.querySelector(`[data-chart-id="${targetId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (attempts < 10) {
        requestAnimationFrame(() => tryScroll(attempts + 1));
      }
    };
    requestAnimationFrame(() => tryScroll());
  }, [scrollToChartId, setScrollToChartId, charts.length]);

  // ── Filters as plain object for chart queries ────────────────────────────
  // Range filters (date_range, numeric) store nested objects under the
  // variable key; the API contract is flat (`<var>_start` / `<var>_end`),
  // so we flatten before sending. See `flattenFilterValues`.
  const filtersObject = useMemo(() => {
    const merged: Record<string, any> = { ...defaultFilterValuesRef.current };
    for (const [k, v] of filterValues) {
      if (v != null) merged[k] = v;
    }
    return flattenFilterValues(merged);
  }, [filterValues, refreshVersion]);

  // ── Chart actions ────────────────────────────────────────────────────────
  const handleEditChart = useCallback((chartId: string) => {
    if (!dashboardId) return;
    navigate(`/dashboards/${dashboardId}/charts/${chartId}/edit`);
  }, [dashboardId, navigate]);

  const handleDeleteChart = useCallback(async (chartId: string) => {
    if (!dashboardId) return;
    try {
      await chartsService.deleteChart(chartId);
      await loadDashboardDetails(dashboardId);
    } catch (err) {
      console.error('Failed to delete chart:', err);
    }
  }, [dashboardId, loadDashboardDetails]);

  const handleDuplicateChart = useCallback(async (chartId: string) => {
    if (!dashboardId) return;
    try {
      const newChart = await chartsService.duplicateChart(chartId);
      await loadDashboardDetails(dashboardId);
      // Scroll the duplicated chart into view after the layout re-renders
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-chart-id="${newChart.id}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    } catch (err) {
      console.error('Failed to duplicate chart:', err);
    }
  }, [dashboardId, loadDashboardDetails]);

  // ── Early returns ────────────────────────────────────────────────────────

  if (isLoadingDetails && !currentDashboardDetails) {
    return (
      <div className="dashboard" data-theme={isDarkMode ? 'dark' : 'light'}>
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <Spinner size="large" label="Loading dashboard..." />
        </div>
      </div>
    );
  }

  if (dashboardError) {
    return (
      <div className="dashboard" data-theme={isDarkMode ? 'dark' : 'light'}>
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Error Loading Dashboard</MessageBarTitle>
            {dashboardError}
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  if (!currentDashboardDetails) {
    return (
      <div className="dashboard" data-theme={isDarkMode ? 'dark' : 'light'}>
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <MessageBar intent="info">
            <MessageBarBody>
              <MessageBarTitle>No Dashboard Selected</MessageBarTitle>
              Select a dashboard from the sidebar to view its contents.
            </MessageBarBody>
          </MessageBar>
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="dashboard" data-theme={isDarkMode ? 'dark' : 'light'} style={{ position: 'relative' }}>
      {filters.length > 0 && (
        <DashboardFilterBar
          dashboardId={dashboardId}
          filters={filters}
          filterValues={filterValues}
          onFilterChange={handleFilterChange}
          onFilterDefault={handleFilterDefault}
          onFilterReset={handleFilterReset}
          onResetAll={handleResetAll}
          onFilterReady={handleFilterReady}
          onRefreshData={handleRefreshData}
        />
      )}

      <div className="dashboard-content" ref={scrollRef}>
        <div className={`dashboard-width-container ${widthMode === 'centered' ? 'dashboard-width-centered' : 'dashboard-width-full'}`}>
          <DashboardGridLayout
            dashboardId={dashboardId}
            charts={charts}
            layout={layoutEditor.layout}
            onLayoutChange={(layout) => runMutation(() => handleLayoutChange(layout))}
            filters={filtersObject}
            filtersReady={filtersReady}
            isDark={isDarkMode}
            dashboardTheme={dashboardSettings.theme}
            animated={dashboardSettings.animations}
            recompile={recompile}
            onEditChart={isReadOnly ? undefined : handleEditChart}
            onDuplicateChart={isReadOnly ? undefined : handleDuplicateChart}
            onDeleteChart={isReadOnly ? undefined : handleDeleteChart}
            onSetAsThumbnail={isReadOnly ? undefined : async (chartId) => {
              try {
                await captureAndUploadThumbnail(chartId, dashboardId);
              } catch (e) {
                console.warn('[Thumbnail] Manual capture failed:', e);
              }
            }}
          />
        </div>
      </div>

      <DashboardSettingsPanel
        isOpen={isSettingsPanelOpen && !isReadOnly}
        onClose={() => setIsSettingsPanelOpen(false)}
        settings={dashboardSettings}
        dashboardName={currentDashboardDetails?.name ?? ''}
        dashboardDescription={currentDashboardDetails?.description ?? ''}
        onSettingsChange={handleSettingsChange}
        onDashboardMetaChange={handleDashboardMetaChange}
      />
      <DashboardShareDialog
        dashboardId={dashboardId}
        open={isShareDialogOpen && !isReadOnly}
        onOpenChange={setIsShareDialogOpen}
      />
    </div>
  );
};

export default Dashboard;